// ============================
// formReducer.ts
// ============================
import { ValuationsType } from '../../utility/types';
import { LspjDataProps } from '../../redux/state/lspjSlice';

export type DecisionMakingState = {
  wanted: boolean | null;
  notes: string | null;
  approachId: string | null;
  valuationType: ValuationsType;
  estimatedValue: number | null;
  estimatedValuePerSqft: number | null;
  fixedCosts: number | null;
  maxBid: number | null;
  maxBidPerSqft: number | null;
  estimatedProfit: number | null;
  roi: number | null;
  formUpdated: boolean;
  [key: string]: string | number | boolean | null;
  userEnteredFixedCosts: boolean | null;
  propertyId: string | null;
};

export const initialState: DecisionMakingState = {
  wanted: null,
  notes: null,
  approachId: null,
  valuationType: ValuationsType.InactiveAvm,
  estimatedValue: null,
  estimatedValuePerSqft: null,
  fixedCosts: null,
  maxBid: null,
  maxBidPerSqft: null,
  estimatedProfit: null,
  roi: null,
  formUpdated: false,
  userEnteredFixedCosts: null,
  propertyId: null,
};

export type DecisionFormDataKeys = keyof DecisionMakingState;

export type DecisionMakingAction =
  | { type: 'SET_FORM_FIELD'; payload: { field: DecisionFormDataKeys; value: any, updateform?: boolean } | { field: DecisionFormDataKeys; value: any }[] }
  | { type: 'INITIALIZE_FORM_KEY'; payload: { field: DecisionFormDataKeys; value: any } | { field: DecisionFormDataKeys; value: any }[] }
  | { type: 'RESET_FORM_UPDATED' }
  | { type: 'SET_ACTIVE_LSPJ'; payload: LspjDataProps | null }
  | { type: 'NEW_ACTIVE_PROPERTY' };

export const reducer = (state: DecisionMakingState, action: DecisionMakingAction): DecisionMakingState => {
  switch (action.type) {
  case 'SET_ACTIVE_LSPJ': {
    const l = action.payload;
    // if we have an lspj with invalid estimated values, null them here.
    let estimatedValue = l?.estimated_value;
    let estimatedValuePerSqft = l?.estimated_value_per_sqft;
    let valuationType = l?.valuations_option || ValuationsType.InactiveAvm;
    if (l?.valuations_option === ValuationsType.UserInput
      && (!estimatedValue || !estimatedValuePerSqft)
    ) {
      estimatedValue = null;
      estimatedValuePerSqft = null;
    }

    return {
      ...state,
      approachId: l?.approach_id || null,
      valuationType: valuationType as ValuationsType,
      estimatedValue: estimatedValue || null,
      estimatedValuePerSqft: estimatedValuePerSqft || null,
      fixedCosts: l?.fix_costs || state.fixedCosts, // This may have already been set by the repairCostsContext
      userEnteredFixedCosts: l?.fix_costs ? true : false,
      maxBid: l?.max_bid || null,
      maxBidPerSqft: l?.max_bid_per_sqft || null,
      estimatedProfit: l?.estimated_profit || null,
      roi: l?.roi || null,
      wanted: l?.wanted || null,
      formUpdated: !l?.approach_id ? true : false, // If the lspj loads and it does not exist yet, we need to set the formUpdated to true.
      propertyId: l?.propertyId || null,
    };
  }

  case 'INITIALIZE_FORM_KEY': {
    if (Array.isArray(action.payload)) {
      const updatedState = { ...state };
      action.payload.forEach(({ field, value }: { field: DecisionFormDataKeys; value: any }) => {
        updatedState[field] = value;
      });
      return { ...updatedState, formUpdated: state.formUpdated || false }; // We don't want to trigger a recalculation when we initialize a key. unless it already has been set to true.
    } else {
      return {
        ...state,
        [action.payload.field]: action.payload.value,
        formUpdated: state.formUpdated || false, // We don't want to trigger a recalculation when we initialize a key. And this should happen before any user input is made.
      };
    }
  }

  case 'SET_FORM_FIELD': {
    if (Array.isArray(action.payload)) {
      const updatedState = { ...state };
      action.payload.forEach(({ field, value }: { field: DecisionFormDataKeys; value: any }) => {
        updatedState[field] = value;
      });
      return { ...updatedState, formUpdated: true };
    } else {
      return {
        ...state,
        [action.payload.field]: action.payload.value,
        formUpdated: action.payload.updateform ?? true,
      };
    }
  }

  case 'NEW_ACTIVE_PROPERTY':
    return { ...initialState, formUpdated: false };

  case 'RESET_FORM_UPDATED':
    return { ...state, formUpdated: false };

  default:
    return state;
  }
};
